/**
 * The Section 4 post-processing stack on Three's node-based RenderPipeline (TSL only; no
 * EffectComposer, no ShaderMaterial):
 *
 *   scene pass (MRT: colour, packed normals, velocity, diffuse) at the render scale
 *   -> GTAO (or SSGI's AO + GI at Ultra), multiplied in while still HDR
 *   -> TAAU: temporal anti-aliasing that also upscales to the drawing-buffer resolution
 *   -> bloom -> depth of field (hangar only) -> motion blur (off by default)
 *   -> AgX tone mapping + sRGB (renderOutput)
 *   -> colour-grading LUT -> chromatic aberration -> vignette -> film grain
 *   (TAA off: FXAA on the display-referred image, then FSR 1 when upscaling)
 *
 * AO and SSGI run before TAAU so the temporal accumulation denoises them (DECISIONS.md).
 */
import type { Data3DTexture, Node, PerspectiveCamera, Scene, WebGPURenderer } from 'three/webgpu';
import { RenderPipeline } from 'three/webgpu';
import {
  convertToTexture,
  diffuseColor,
  float,
  int,
  length,
  mrt,
  normalView,
  output,
  packNormalToRGB,
  pass,
  renderOutput,
  sample,
  smoothstep,
  texture3D,
  unpackRGBToNormal,
  uv,
  vec2,
  vec4,
  velocity,
} from 'three/tsl';
import { bloom } from 'three/addons/tsl/display/BloomNode.js';
import { chromaticAberration } from 'three/addons/tsl/display/ChromaticAberrationNode.js';
import { denoise } from 'three/addons/tsl/display/DenoiseNode.js';
import { dof } from 'three/addons/tsl/display/DepthOfFieldNode.js';
import { film } from 'three/addons/tsl/display/FilmNode.js';
import { fsr1 } from 'three/addons/tsl/display/FSR1Node.js';
import { fxaa } from 'three/addons/tsl/display/FXAANode.js';
import { ao } from 'three/addons/tsl/display/GTAONode.js';
import { lut3D } from 'three/addons/tsl/display/Lut3DNode.js';
import { motionBlur } from 'three/addons/tsl/display/MotionBlur.js';
import { ScaledSsgiNode } from './ssgi.js';
import { taau } from 'three/addons/tsl/display/TAAUNode.js';

import type { EffectConfig } from '../../settings/presets.js';

/** A colour node: the typings put swizzles and arithmetic on the typed form only. */
type Vec4Node = Node<'vec4'>;

/** Several addon effects are declared as untyped nodes although they emit vec4 at runtime. */
function asVec4(node: Node): Vec4Node {
  return node as Vec4Node;
}

export interface PostGraphInputs {
  renderer: WebGPURenderer;
  scene: Scene;
  camera: PerspectiveCamera;
  config: EffectConfig;
  lut: Data3DTexture;
  /** Initial render scale (dynamic resolution updates it through setScale). */
  scale: number;
  /** A node for the depth-of-field focus distance, when the owner moves it every frame. */
  dofFocus?: Node;
}

export interface PostGraph {
  pipeline: RenderPipeline;
  /** Changes the internal resolution without rebuilding the graph. */
  setScale(scale: number): void;
  dispose(): void;
}

interface Disposable {
  dispose(): void;
}

function vignette(color: Vec4Node, strength: number): Vec4Node {
  const distance = length(uv().sub(0.5)).mul(1.4142);
  const mask = smoothstep(0.35, 1.05, distance).mul(strength);
  return vec4(color.rgb.mul(mask.oneMinus()), color.a);
}

export function buildPostGraph(inputs: PostGraphInputs): PostGraph {
  const { renderer, scene, camera, config, lut } = inputs;
  const pipeline = new RenderPipeline(renderer);
  // Tone mapping and colour space are applied explicitly mid-chain (see renderOutput below).
  pipeline.outputColorTransform = false;
  const disposables: Disposable[] = [];

  const scenePass = pass(scene, camera);
  scenePass.setResolutionScale(inputs.scale);
  disposables.push(scenePass);

  const needNormal = config.ao !== null || config.ssgi !== null;
  const needVelocity = config.aa === 'taau' || config.motionBlur !== null;
  const layout: Record<string, Node> = { output };
  if (needNormal) layout['normal'] = packNormalToRGB(normalView);
  if (needVelocity) layout['velocity'] = velocity;
  if (config.ssgi) layout['diffuseColor'] = diffuseColor;
  scenePass.setMRT(mrt(layout));

  const sceneColor = scenePass.getTextureNode('output');
  const sceneDepth = scenePass.getTextureNode('depth');
  const sceneVelocity = needVelocity ? scenePass.getTextureNode('velocity') : null;
  const sceneNormal: Node | null = needNormal
    ? sample((coord) => unpackRGBToNormal(scenePass.getTextureNode('normal').sample(coord)))
    : null;

  let color: Vec4Node = sceneColor;
  let aoPass: ReturnType<typeof ao> | null = null;
  let giPass: ScaledSsgiNode | null = null;

  // --- HDR, internal resolution ---
  if (config.ssgi && sceneNormal) {
    // Sized at the render scale (Phase 8): dynamic resolution shrinks the GI with the scene.
    giPass = new ScaledSsgiNode(sceneColor, sceneDepth, sceneNormal, camera);
    giPass.scale = inputs.scale;
    giPass.sliceCount.value = config.ssgi.sliceCount;
    giPass.stepCount.value = config.ssgi.stepCount;
    disposables.push(giPass);
    const diffuse = scenePass.getTextureNode('diffuseColor');
    const gi = giPass.getGINode();
    const occlusion = giPass.getAONode();
    color = vec4(sceneColor.rgb.mul(occlusion.r).add(diffuse.rgb.mul(gi.rgb)), sceneColor.a);
  } else if (config.ao && sceneNormal) {
    aoPass = ao(sceneDepth, sceneNormal, camera);
    aoPass.resolutionScale = config.ao.scale * inputs.scale;
    disposables.push(aoPass);
    let occlusion: Vec4Node = aoPass.getTextureNode();
    if (config.ao.denoise) {
      const denoised = denoise(occlusion, sceneDepth, sceneNormal, camera);
      disposables.push(denoised);
      occlusion = asVec4(denoised);
    }
    color = vec4(sceneColor.rgb.mul(occlusion.r), sceneColor.a);
  }

  // --- temporal anti-aliasing and upscale to the drawing buffer ---
  if (config.aa === 'taau' && sceneVelocity) {
    const taauPass = taau(convertToTexture(color), sceneDepth, sceneVelocity, camera);
    disposables.push(taauPass);
    color = taauPass;
  }

  // --- HDR, output resolution ---
  if (config.bloom) {
    const bloomPass = bloom(
      color,
      config.bloom.strength,
      config.bloom.radius,
      config.bloom.threshold,
    );
    disposables.push(bloomPass);
    color = color.add(bloomPass);
  }
  if (config.dof) {
    const dofPass = dof(
      color,
      scenePass.getViewZNode(),
      inputs.dofFocus ?? config.dof.focusDistance,
      config.dof.focalLength,
      config.dof.bokehScale,
    );
    disposables.push(dofPass);
    color = asVec4(dofPass);
  }
  if (config.motionBlur && sceneVelocity) {
    color = motionBlur(color, sceneVelocity, int(config.motionBlur.samples));
  }

  // --- AgX tone mapping + sRGB ---
  color = renderOutput(color);

  // --- display-referred ---
  if (config.lut) {
    const lutPass = lut3D(color, texture3D(lut), lut.image.width, float(config.lut.intensity));
    disposables.push(lutPass);
    color = asVec4(lutPass);
  }
  if (config.chromaticAberration) {
    // The helper wraps `center` in nodeObject(), so it must be a real node, never null.
    color = asVec4(
      chromaticAberration(
        color,
        float(config.chromaticAberration.strength),
        vec2(0.5, 0.5),
        float(1.015),
      ),
    );
  }
  if (config.vignette) color = vignette(color, config.vignette.strength);
  if (config.grain) color = asVec4(film(color, float(config.grain.intensity)));
  if (config.aa === 'fxaa') {
    const fxaaPass = fxaa(color);
    disposables.push(fxaaPass);
    color = asVec4(fxaaPass);
    // FSR 1 (edge-adaptive upscale + sharpening) always follows FXAA so a later render-scale
    // change never needs a graph rebuild; at scale 1.0 it is one cheap sharpening pass.
    const upscale = fsr1(color, 0.2);
    disposables.push(upscale);
    color = upscale;
  }

  pipeline.outputNode = color;
  pipeline.needsUpdate = true;

  return {
    pipeline,
    setScale(scale) {
      scenePass.setResolutionScale(scale);
      if (aoPass && config.ao) aoPass.resolutionScale = config.ao.scale * scale;
      if (giPass) giPass.scale = scale;
    },
    dispose() {
      for (const d of disposables.reverse()) d.dispose();
      pipeline.dispose();
      camera.clearViewOffset();
    },
  };
}
